/**
 * StatsCard Component
 * Metric card with icon, value, and trend indicator
 * Styled to match enterprise SaaS dashboard design
 */

import React from 'react';
import { Box, Paper, Typography, Avatar } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export interface StatsCardProps {
  title: string;
  value: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  icon: React.ReactNode;
  iconColor: string;
  iconBgColor: string;
}

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  trend,
  icon,
  iconColor,
  iconBgColor,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: '100%',
        border: '1px solid',
        borderColor: '#e5e7eb',
        borderRadius: 1.5,
        backgroundColor: '#ffffff',
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: iconBgColor,
              color: iconColor,
              width: 44,
              height: 44,
              '& .MuiSvgIcon-root': {
                fontSize: '1.25rem',
              },
            }}
          >
            {icon}
          </Avatar>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: '0.875rem',
              color: '#6b7280',
            }}
          >
            {title}
          </Typography>
        </Box>
        <MoreVertIcon
          sx={{
            fontSize: 20,
            color: '#9ca3af',
            cursor: 'pointer',
            '&:hover': {
              color: '#6b7280',
            },
          }}
        />
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          fontSize: '1.875rem',
          color: '#111827',
          mb: 0.5,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>

      {trend && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {trend.isPositive ? (
            <TrendingUpIcon sx={{ fontSize: 14, color: '#10b981' }} />
          ) : (
            <TrendingDownIcon sx={{ fontSize: 14, color: '#ef4444' }} />
          )}
          <Typography
            variant="caption"
            sx={{
              color: trend.isPositive ? '#10b981' : '#ef4444',
              fontWeight: 500,
              fontSize: '0.75rem',
            }}
          >
            {trend.isPositive ? '+' : ''}{trend.value}% from the last month
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default StatsCard;
